// src/pages/Register.js
import React, { useState, useRef } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import emailjs from '@emailjs/browser';
import { auth } from '../config/firebase.config';
import { supabase } from '../config/supabase.config';
import { emailConfig } from '../config/emailjs.config';
import './Register.css';

function Register({ onRegisterSuccess, onSwitchToLogin }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedRole, setSelectedRole] = useState('owner');
  
  const emailRef = useRef();
  const passwordRef = useRef();
  const nameRef = useRef();
  const flatRef = useRef();
  const phoneRef = useRef();

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const email = emailRef.current?.value;
    const password = passwordRef.current?.value;
    const name = nameRef.current?.value;
    const flat = flatRef.current?.value;
    const phone = phoneRef.current?.value;
    
    // Validation
    if (!email || !password || !name || !flat || !phone) {
      setError('All fields are required');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (phone.length < 10) {
      setError('Please enter valid 10 digit phone number');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Send verification email
      await sendEmailVerification(firebaseUser);
      
      // Save to Supabase database
      const { data, error: supabaseError } = await supabase
        .from('residents')
        .insert([
          {
            firebase_uid: firebaseUser.uid,
            email: email,
            name: name,
            flat_number: flat,
            phone: phone,
            role: selectedRole,
            status: 'pending',
            account_status: 'active',
            created_at: new Date().toISOString()
          }
        ])
        .select()
        .single();
      
      if (supabaseError) {
        setError(`User created but database save failed: ${supabaseError.message}`);
      } else {
        // Fetch superadmin and send notification
        try {
          const { data: superadminData, error: superadminError } = await supabase
            .from('residents')
            .select('email, name')
            .eq('role', 'superadmin')
            .eq('account_status', 'active')
            .single();
          
          if (superadminData && !superadminError) {
            await emailjs.send(
              emailConfig.serviceId,
              emailConfig.templateId,
              {
                to_email: superadminData.email,
                to_name: superadminData.name,
                user_name: name,
                user_email: email,
                user_phone: phone,
                user_flat: flat,
                user_role: selectedRole,
                registration_time: new Date().toLocaleString()
              }
            );
            setSuccess('Registration successful! Check your email for verification. Admin has been notified for approval.');
          } else {
            setSuccess('Registration successful! Check your email for verification. Please contact admin for approval.');
          }
        } catch (emailError) {
          setSuccess('Registration successful! Check your email for verification.');
        }
      }
      
      // Clear form and redirect
      setTimeout(() => {
        onSwitchToLogin();
      }, 3000);
      
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        setError('Email already registered. Please login.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak.');
      } else {
        setError(error.message);
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="register-page">
      <div className="register-split-container">
        {/* Left Panel - Branding */}
        <div className="register-left-panel">
          <div className="branding-content">
            <div className="brand-header">
              <div className="brand-icon">🏢</div>
              <h1>Srinivasa Flats</h1>
              <p>Community Management System</p>
            </div>
            
            <div className="benefits-list">
              <h3>Join Our Community</h3>
              <div className="benefit-item">
                <span className="benefit-icon">📱</span>
                <div>
                  <strong>Digital Payments</strong>
                  <p>Pay maintenance online securely</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon">📊</span>
                <div>
                  <strong>Transparent Management</strong>
                  <p>Track all community expenses</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon">🔔</span>
                <div>
                  <strong>Instant Updates</strong>
                  <p>Get notifications for important events</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon">🛠️</span>
                <div>
                  <strong>Service Requests</strong>
                  <p>Raise and track maintenance issues</p>
                </div>
              </div>
            </div>
            
            <div className="brand-footer">
              <p>Powered by <strong>STRONARIX</strong></p>
            </div>
          </div>
        </div>
        
        {/* Right Panel - Registration Form */}
        <div className="register-right-panel">
          <div className="register-form-container">
            <div className="form-header">
              <h2>Create Account</h2>
              <p>Join your community management system</p>
            </div>
            
            <form className="register-form" onSubmit={handleRegister}>
              {error && (
                <div className="alert-error">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}
              
              {success && (
                <div className="alert-success">
                  <span className="success-icon">✅</span>
                  {success}
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    ref={nameRef}
                    type="text"
                    className="form-control"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Flat Number *</label>
                  <input
                    ref={flatRef}
                    type="text"
                    className="form-control"
                    placeholder="e.g., A-101"
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  ref={phoneRef}
                  type="tel"
                  className="form-control"
                  placeholder="10 digit phone number"
                  required
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  }}
                />
              </div>
              
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  ref={emailRef}
                  type="email"
                  className="form-control"
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Password *</label>
                <input
                  ref={passwordRef}
                  type="password"
                  className="form-control"
                  placeholder="Minimum 6 characters"
                  required
                />
              </div>
              
              {/* Role Selection */}
              <div className="role-selection">
                <label>I am a:</label>
                <div className="role-buttons">
                  <button
                    type="button"
                    className={`role-btn ${selectedRole === 'owner' ? 'active' : ''}`}
                    onClick={() => setSelectedRole('owner')}
                  >
                    <span className="role-icon">🏠</span>
                    <span>Owner</span>
                  </button>
                  <button
                    type="button"
                    className={`role-btn ${selectedRole === 'tenant' ? 'active' : ''}`}
                    onClick={() => setSelectedRole('tenant')}
                  >
                    <span className="role-icon">🔑</span>
                    <span>Tenant</span>
                  </button>
                </div>
              </div>
              
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Creating Account...' : 'Register'}
              </button>
              
              <div className="form-divider">
                <span>Already have an account?</span>
              </div>
              
              <button type="button" className="btn-secondary" onClick={onSwitchToLogin}>
                Back to Login
              </button>
            </form>
            
            <div className="terms-footer">
              <p>By registering, you agree to our Terms of Service and Privacy Policy</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;